import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CampaignScheduleImportCandidate } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/types';

import OffersService from '~/services/OffersService';

import { offerQueryKeys } from './useGetOffer';

export interface CampaignScheduleImportResult {
  candidate: CampaignScheduleImportCandidate;
  success: boolean;
  error?: string;
}

export interface CampaignScheduleImportResponse {
  results: Array<CampaignScheduleImportResult>;
  // Selected keys svc-offer couldn't match against a freshly re-derived candidate — Salesforce or
  // the offer's schedules changed between this admin's preview and their import click.
  skippedKeys: Array<string>;
}

// svc-offer re-derives and submits server-side — this just posts the selected candidate keys.
export function useImportCampaignSchedules(offerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      selectedCandidates: Array<CampaignScheduleImportCandidate>,
    ): Promise<CampaignScheduleImportResponse> => {
      if (!offerId) {
        throw new Error('offerId is required');
      }

      return OffersService.importCampaignSchedules(
        offerId,
        selectedCandidates.map((candidate) => candidate.key),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.scheduleLogs(offerId) });
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.campaignScheduleImportCandidates(offerId) });
    },
  });
}
